/**
 * Keeps the ids of the products a shopper viewed on this device in localStorage, most recent first.
 *
 * Consumers: the product page (`snippets/scripts.liquid` adds the current product), the search
 * modal empty state (`assets/predictive-search.js`, which also clears the history) and the
 * Recently viewed section (`assets/recently-viewed.js`, which listens for `CLEARED_EVENT`).
 *
 * Storage can be unavailable (privacy modes, blocked site data) or hold unexpected values, so every
 * access is guarded: reads fall back to an empty list and failed writes are ignored.
 */
export class RecentlyViewed {
  /** @static @constant {string} The localStorage key that holds the viewed product ids */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of product ids to keep */
  static #MAX_PRODUCTS = 12;

  /**
   * The maximum number of product ids kept in the history.
   * @returns {number}
   */
  static get maxProducts() {
    return RecentlyViewed.#MAX_PRODUCTS;
  }

  /**
   * Adds a product to the start of the recently viewed products list.
   * @param {string | number} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    const id = String(productId ?? '').trim();
    if (!RecentlyViewed.#isValidId(id)) return;

    const viewedProducts = [id, ...RecentlyViewed.getProducts().filter((viewedId) => viewedId !== id)].slice(
      0,
      RecentlyViewed.#MAX_PRODUCTS
    );

    try {
      localStorage.setItem(RecentlyViewed.#STORAGE_KEY, JSON.stringify(viewedProducts));
    } catch {
      // Storage is unavailable or full; the history simply isn't updated.
    }
  }

  /**
   * The event dispatched on `document` after the history is cleared, so other consumers on the
   * page (the Recently viewed section) stop showing the cleared products.
   * @returns {string}
   */
  static get CLEARED_EVENT() {
    return 'recently-viewed:cleared';
  }

  /**
   * Removes every product from the recently viewed products list.
   */
  static clearProducts() {
    try {
      localStorage.removeItem(RecentlyViewed.#STORAGE_KEY);
    } catch {
      // Storage is unavailable; there is nothing to clear.
    }

    document.dispatchEvent(new CustomEvent(RecentlyViewed.CLEARED_EVENT));
  }

  /**
   * Retrieves the recently viewed product ids, most recent first.
   * Invalid and duplicate entries are dropped, and the list never exceeds the history size.
   * @returns {string[]} The list of viewed product ids.
   */
  static getProducts() {
    /** @type {unknown} */
    let stored;

    try {
      stored = JSON.parse(localStorage.getItem(RecentlyViewed.#STORAGE_KEY) || '[]');
    } catch {
      return [];
    }

    if (!Array.isArray(stored)) return [];

    /** @type {string[]} */
    const ids = [];

    for (const value of stored) {
      const id = String(value);
      if (!RecentlyViewed.#isValidId(id) || ids.includes(id)) continue;

      ids.push(id);
      if (ids.length === RecentlyViewed.#MAX_PRODUCTS) break;
    }

    return ids;
  }

  /**
   * Checks that a value is a numeric product id.
   * @param {string} id - The value to check.
   * @returns {boolean}
   */
  static #isValidId(id) {
    return /^\d+$/.test(id);
  }
}
